/**
 * N2A (Juni 2026) – OPS Sales-Katalog (STRIKT READ-ONLY).
 *
 * Lesende Sicht auf den DB-Sales-Katalog (`sales_catalog_versions`) über die
 * beiden bestehenden Ops-GET-Endpunkte (Versionsliste + Versionsdetail).
 *
 * N2C: einzige Draft-Schreibaktion (Draft aus Version erstellen).
 * N2E3: GENAU EIN bearbeitbares Produktfeld (`short_description`) — nur bei
 * Draft-Versionen, über den sicheren N2E2-PUT-Endpunkt (CAS, draft-only,
 * vollständige Produktliste). Kein weiteres Feld, keine Produktanlage/-löschung.
 * N2F3: Publish-Oberfläche — „Veröffentlichen" NUR bei Draft-Status, über den
 * in N2F2 gehärteten Backend-Endpunkt (CAS via expected_content_hash), mit
 * Bestätigungsdialog, busy-Guard und verbindlichem GET-Reload (kein
 * optimistisches Update). Backend-Fehler werden unverändert angezeigt.
 */
import { useCallback, useEffect, useState } from "react";

import { AlertTriangle, BookOpenCheck, CheckCircle2, Eye, FilePlus2, Loader2, PackagePlus, Pencil, RefreshCw, UploadCloud } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";

import { opsApi, opsErrorMessage } from "@/lib/opsApi";
import { ProduktAnlageMaske } from "@/components/ProduktAnlageMaske";

// N2E3: maschinenlesbare Backend-Fehlercodes aus dem detail-Text des
// N2E2-Endpunkts erkennen (bestehendes Fehlerformat: Code-String im Detail).
function katalogFehlerCode(err) {
  const detail = err?.response?.data?.detail;
  if (typeof detail !== "string") return null;
  if (detail.includes("catalog_draft_stale")) return "catalog_draft_stale";
  if (detail.includes("catalog_version_not_draft")) return "catalog_version_not_draft";
  if (detail.includes("catalog_product_set_changed")) return "catalog_product_set_changed";
  if (detail.includes("catalog_product_already_exists")) return "catalog_product_already_exists";
  return null;
}

// Gespeicherte Statuswerte bleiben sichtbar; deutsche Anzeige nur ergänzend.
const STATUS_STYLE = {
  published: "bg-emerald-100 text-emerald-800 border-emerald-200",
  draft: "bg-amber-100 text-amber-900 border-amber-200",
  archived: "bg-slate-100 text-slate-600 border-slate-200",
};
const STATUS_DE = {
  published: "veröffentlicht",
  draft: "Entwurf",
  archived: "archiviert",
};

const NICHT_GELIEFERT = "Von der API nicht geliefert";
const NICHT_HINTERLEGT = "Nicht hinterlegt";

// Rohwert aus der API-Antwort; Feld fehlt → „Von der API nicht geliefert",
// Feld leer/null → „Nicht hinterlegt". Keine erfundenen Ersatzwerte.
function feldWert(obj, key) {
  if (!obj || !Object.prototype.hasOwnProperty.call(obj, key)) return NICHT_GELIEFERT;
  const v = obj[key];
  if (v === null || v === undefined || v === "") return NICHT_HINTERLEGT;
  return v;
}

// Boolesche Werte eindeutig als Ja/Nein; nicht-boolesche Werte unverändert.
function boolAnzeige(v) {
  if (v === true) return "Ja";
  if (v === false) return "Nein";
  return v;
}

// Cent-Rohwert bleibt sichtbar; Eurobetrag nur ergänzend und nur bei
// eindeutigem EUR-Netto-Kontext (Feldnamen *_net_cents). Keine Rundung,
// keine Berechnung über die reine Darstellung hinaus.
function centAnzeige(cents, currency) {
  if (!Number.isInteger(cents)) return String(cents);
  if (currency !== "EUR") return String(cents);
  const euro = (cents / 100).toFixed(2).replace(".", ",");
  return `${cents} Cent netto · ${euro} EUR netto`;
}

// N2B: sichere Read-only-Darstellung strukturierter Felder (entitlements,
// metadata) als formatierter JSON-Block. Keine semantische Umformung, keine
// Schlüsselauswahl; fehlerhafte Strukturen führen nicht zum Absturz.
function safeJson(v) {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return null;
  }
}

function StrukturWert({ product, feldKey }) {
  if (!Object.prototype.hasOwnProperty.call(product, feldKey)) return NICHT_GELIEFERT;
  const v = product[feldKey];
  if (v === null || v === undefined) return NICHT_HINTERLEGT;
  if (typeof v !== "object") return String(v);
  if (Object.keys(v).length === 0) return NICHT_HINTERLEGT;
  const json = safeJson(v);
  if (json === null) return "Struktur nicht darstellbar";
  return (
    <pre className="font-mono text-xs bg-slate-50 border border-slate-100 rounded-md p-2 whitespace-pre-wrap break-all max-h-64 overflow-auto">
      {json}
    </pre>
  );
}

function StatusBadgeSc({ status }) {
  const style = STATUS_STYLE[status] || "bg-slate-100 text-slate-600 border-slate-200";
  const de = STATUS_DE[status];
  return (
    <Badge
      variant="outline"
      className={`${style} font-mono text-[11px]`}
      data-testid={`ops-sales-catalog-status-${status}`}
    >
      {status}
      {de ? ` · ${de}` : ""}
    </Badge>
  );
}

function FeldZeile({ feldId, label, children }) {
  return (
    <div className="flex flex-col gap-0.5 py-1.5 border-b border-slate-100 last:border-b-0">
      <dt className="text-[11px] uppercase tracking-wide text-slate-400">{label}</dt>
      <dd className="text-sm text-slate-800 break-all" data-testid={feldId}>
        {children}
      </dd>
    </div>
  );
}

// N2E3/O1H: lokal begrenzter Editor für die im Draft nachpflegbaren
// Produktfelder eines bestehenden Draft-Produkts:
//   * `short_description` (N2E3)
//   * `kategorie` (O1H, deutsche Werte)
//   * `verkaufsart` (O1H, deutsche Werte)
// Zwei Schritte: Bearbeiten → Vorher-Nachher-Prüfung. Kein generischer
// Vollprodukt-Editor, kein separater Editor pro Feld — alle drei Felder
// werden im selben sicheren CAS-Ablauf gespeichert. Bei Alt-Produkten steht
// der Ausgangswert der O1H-Felder auf „Nicht gesetzt" (null), Betreiber
// wählt bewusst.
const KATEGORIE_WERTE = ["Hauptprodukt", "Zusatzprodukt", "Service"];
const VERKAUFSART_WERTE = ["Einmalkauf", "Abonnement"];

function anzeigeWert(v) {
  return v === "" || v === null || v === undefined ? NICHT_HINTERLEGT : v;
}

// OPS-01 (Okt 2026) — Erweiterung des Draft-Produkt-Editors um die vier im Draft
// nachpflegbaren Preisfelder: per_seat_net_cents, billing_intervals,
// stripe_price_ids, long_description. Backend-Vertrag (PUT N2E2) unverändert.
const INTERVAL_WERTE = ["monthly", "yearly", "one_time"];

function arrEqualIgnoreOrder(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((v, i) => v === sb[i]);
}

function stripePairsToObject(pairs) {
  const obj = {};
  for (const { key, value } of pairs || []) {
    const k = (key || "").trim();
    if (k !== "") obj[k] = value;
  }
  return obj;
}

function shallowObjEqual(a, b) {
  const ka = Object.keys(a || {});
  const kb = Object.keys(b || {});
  if (ka.length !== kb.length) return false;
  return ka.every((k) => (a || {})[k] === (b || {})[k]);
}

function perSeatValidierung(raw) {
  const s = (raw ?? "").trim();
  if (s === "") return null;
  if (!/^\d+$/.test(s)) return "per_seat_net_cents muss eine ganze Zahl ≥ 0 oder leer sein.";
  return null;
}

// Clientseitige Konsistenzsperre (OPS-01, Punkt 2): Schlüssel der
// stripe_price_ids müssen zu den gewählten billing_intervals passen und
// umgekehrt. HINWEIS: Der gemeinsame Backend-Validator (validate_product)
// prüft diese Kombination NICHT — diese Sperre lebt daher nur im UI.
function stripeIntervalValidierung(intervals, pairs) {
  const keys = (pairs || []).map((p) => (p.key || "").trim()).filter((k) => k !== "");
  if (new Set(keys).size !== keys.length) return "Doppelter Stripe-Schlüssel (Intervall).";
  for (const { key, value } of pairs || []) {
    const k = (key || "").trim();
    if (k === "") continue;
    if (!INTERVAL_WERTE.includes(k))
      return `Unzulässiger Stripe-Schlüssel: ${k} (erlaubt: monthly/yearly/one_time).`;
    if (!intervals.includes(k)) return `Price-ID ohne passendes Intervall: ${k}.`;
    if ((value || "").trim() === "") return `Price-ID-Wert fehlt für Intervall ${k}.`;
  }
  for (const iv of intervals) {
    if (!keys.includes(iv)) return `Intervall ohne Price-ID: ${iv}.`;
  }
  return null;
}

function KurzbeschreibungEditor({
  product,
  value,
  original,
  kategorieValue,
  kategorieOriginal,
  verkaufsartValue,
  verkaufsartOriginal,
  longDescValue,
  longDescOriginal,
  perSeatValue,
  perSeatOriginal,
  intervalsValue,
  intervalsOriginal,
  stripePairs,
  stripeOriginal,
  validationError,
  dirty,
  step,
  busy,
  err,
  errCode,
  onChange,
  onKategorieChange,
  onVerkaufsartChange,
  onLongDescChange,
  onPerSeatChange,
  onToggleInterval,
  onStripeKeyChange,
  onStripeValueChange,
  onStripeAdd,
  onStripeRemove,
  onCancel,
  onReview,
  onBack,
  onSave,
  onReloadDraft,
}) {
  const konfliktReload =
    errCode === "catalog_draft_stale" || errCode === "catalog_product_set_changed";
  return (
    <div
      className="mt-3 rounded-md border border-amber-200 bg-amber-50/50 px-3 py-3 space-y-3"
      data-testid="ops-sales-catalog-shortdesc-editor"
    >
      <p className="text-xs font-semibold text-slate-700">
        Draft-Produkt bearbeiten (Kurzbeschreibung, Kategorie, Verkaufsart,
        Langbeschreibung, Preis, Intervalle, Stripe-Price-IDs)
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
        <div>
          <span className="text-[11px] uppercase tracking-wide text-slate-400">product_id</span>
          <p className="font-mono text-slate-800" data-testid="ops-sales-catalog-shortdesc-editor-pid">
            {product.product_id}
          </p>
        </div>
        <div>
          <span className="text-[11px] uppercase tracking-wide text-slate-400">label</span>
          <p className="text-slate-800" data-testid="ops-sales-catalog-shortdesc-editor-label">
            {anzeigeWert(product.label)}
          </p>
        </div>
      </div>

      {step === "edit" && (
        <div className="space-y-3">
          <div>
            <span className="text-[11px] uppercase tracking-wide text-slate-400">
              Bisherige Kurzbeschreibung (gespeichert)
            </span>
            <p className="text-sm text-slate-700" data-testid="ops-sales-catalog-shortdesc-editor-original">
              {anzeigeWert(original)}
            </p>
          </div>
          <Textarea
            data-testid="ops-sales-catalog-shortdesc-input"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={3}
            className="bg-white"
          />

          {/* O1H — kategorie: Dropdown mit strikt deutschen Werten. Bei Alt-
              Produkten steht der Ausgangswert auf „Nicht gesetzt" (null). */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1" data-testid="ops-sales-catalog-shortdesc-kategorie-row">
              <span className="text-[11px] uppercase tracking-wide text-slate-400">
                Bisherige kategorie
              </span>
              <p className="text-sm text-slate-700"
                 data-testid="ops-sales-catalog-shortdesc-editor-kategorie-original">
                {anzeigeWert(kategorieOriginal)}
              </p>
              <select
                data-testid="ops-sales-catalog-shortdesc-kategorie-input"
                value={kategorieValue ?? ""}
                onChange={(e) => onKategorieChange(e.target.value === "" ? null : e.target.value)}
                className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
              >
                <option value="">— nicht gesetzt —</option>
                {KATEGORIE_WERTE.map((k) => (
                  <option
                    key={k}
                    value={k}
                    data-testid={`ops-sales-catalog-shortdesc-kategorie-option-${k}`}
                  >
                    {k}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1" data-testid="ops-sales-catalog-shortdesc-verkaufsart-row">
              <span className="text-[11px] uppercase tracking-wide text-slate-400">
                Bisherige verkaufsart
              </span>
              <p className="text-sm text-slate-700"
                 data-testid="ops-sales-catalog-shortdesc-editor-verkaufsart-original">
                {anzeigeWert(verkaufsartOriginal)}
              </p>
              <select
                data-testid="ops-sales-catalog-shortdesc-verkaufsart-input"
                value={verkaufsartValue ?? ""}
                onChange={(e) => onVerkaufsartChange(e.target.value === "" ? null : e.target.value)}
                className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
              >
                <option value="">— nicht gesetzt —</option>
                {VERKAUFSART_WERTE.map((v) => (
                  <option
                    key={v}
                    value={v}
                    data-testid={`ops-sales-catalog-shortdesc-verkaufsart-option-${v}`}
                  >
                    {v}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* OPS-01 — Langbeschreibung (long_description) */}
          <div className="space-y-1" data-testid="ops-sales-catalog-longdesc-row">
            <span className="text-[11px] uppercase tracking-wide text-slate-400">
              Bisherige long_description
            </span>
            <p
              className="text-sm text-slate-700"
              data-testid="ops-sales-catalog-editor-longdesc-original"
            >
              {anzeigeWert(longDescOriginal)}
            </p>
            <Textarea
              data-testid="ops-sales-catalog-longdesc-input"
              value={longDescValue}
              onChange={(e) => onLongDescChange(e.target.value)}
              rows={3}
              className="bg-white"
            />
          </div>

          {/* OPS-01 — per_seat_net_cents (Ganzzahl ≥ 0 oder leer) */}
          <div className="space-y-1" data-testid="ops-sales-catalog-perseat-row">
            <span className="text-[11px] uppercase tracking-wide text-slate-400">
              Bisherige per_seat_net_cents
            </span>
            <p
              className="text-sm text-slate-700"
              data-testid="ops-sales-catalog-editor-perseat-original"
            >
              {perSeatOriginal === null || perSeatOriginal === undefined
                ? NICHT_HINTERLEGT
                : String(perSeatOriginal)}
            </p>
            <input
              data-testid="ops-sales-catalog-perseat-input"
              type="text"
              inputMode="numeric"
              value={perSeatValue}
              onChange={(e) => onPerSeatChange(e.target.value)}
              placeholder="z. B. 490 (leer = kein Preis)"
              className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm font-mono"
            />
          </div>

          {/* OPS-01 — billing_intervals (Checkboxen monthly/yearly/one_time) */}
          <div className="space-y-1" data-testid="ops-sales-catalog-intervals-row">
            <span className="text-[11px] uppercase tracking-wide text-slate-400">
              Bisherige billing_intervals
            </span>
            <p
              className="text-sm text-slate-700"
              data-testid="ops-sales-catalog-editor-intervals-original"
            >
              {intervalsOriginal && intervalsOriginal.length
                ? intervalsOriginal.join(", ")
                : NICHT_HINTERLEGT}
            </p>
            <div className="flex flex-wrap gap-3">
              {INTERVAL_WERTE.map((iv) => (
                <label
                  key={iv}
                  className="flex items-center gap-1.5 text-sm text-slate-700"
                >
                  <input
                    data-testid={`ops-sales-catalog-interval-input-${iv}`}
                    type="checkbox"
                    checked={intervalsValue.includes(iv)}
                    onChange={() => onToggleInterval(iv)}
                  />
                  {iv}
                </label>
              ))}
            </div>
          </div>

          {/* OPS-01 — stripe_price_ids (Schlüssel/Wert: hinzufügen/ändern/entfernen) */}
          <div className="space-y-1" data-testid="ops-sales-catalog-stripe-row">
            <span className="text-[11px] uppercase tracking-wide text-slate-400">
              Bisherige stripe_price_ids
            </span>
            <p
              className="text-sm text-slate-700"
              data-testid="ops-sales-catalog-editor-stripe-original"
            >
              {stripeOriginal && Object.keys(stripeOriginal).length
                ? Object.entries(stripeOriginal)
                    .map(([k, v]) => `${k}: ${v}`)
                    .join(", ")
                : NICHT_HINTERLEGT}
            </p>
            <div className="space-y-2" data-testid="ops-sales-catalog-stripe-rows">
              {stripePairs.map((pair, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2"
                  data-testid={`ops-sales-catalog-stripe-pair-${i}`}
                >
                  <input
                    data-testid={`ops-sales-catalog-stripe-key-${i}`}
                    type="text"
                    value={pair.key}
                    onChange={(e) => onStripeKeyChange(i, e.target.value)}
                    placeholder="Intervall"
                    className="h-9 w-40 rounded-md border border-slate-200 bg-white px-2 text-sm font-mono"
                  />
                  <input
                    data-testid={`ops-sales-catalog-stripe-value-${i}`}
                    type="text"
                    value={pair.value}
                    onChange={(e) => onStripeValueChange(i, e.target.value)}
                    placeholder="price_…"
                    className="h-9 flex-1 rounded-md border border-slate-200 bg-white px-2 text-sm font-mono"
                  />
                  <Button
                    data-testid={`ops-sales-catalog-stripe-remove-${i}`}
                    variant="outline"
                    size="sm"
                    onClick={() => onStripeRemove(i)}
                    disabled={busy}
                  >
                    Entfernen
                  </Button>
                </div>
              ))}
              <Button
                data-testid="ops-sales-catalog-stripe-add"
                variant="outline"
                size="sm"
                onClick={onStripeAdd}
                disabled={busy}
              >
                Price-ID hinzufügen
              </Button>
            </div>
          </div>

          {validationError && (
            <p
              className="text-sm text-red-700 flex items-center gap-2"
              data-testid="ops-sales-catalog-editor-validation-error"
            >
              <AlertTriangle className="w-4 h-4 shrink-0" />
              {validationError}
            </p>
          )}

          <div className="flex items-center gap-2">
            <Button
              data-testid="ops-sales-catalog-shortdesc-cancel"
              variant="outline"
              size="sm"
              onClick={onCancel}
              disabled={busy}
            >
              Abbrechen
            </Button>
            <Button
              data-testid="ops-sales-catalog-shortdesc-review-button"
              size="sm"
              onClick={onReview}
              disabled={!dirty || busy || !!validationError}
            >
              Änderung prüfen
            </Button>
          </div>
        </div>
      )}

      {step === "review" && (
        <div className="space-y-2" data-testid="ops-sales-catalog-shortdesc-review">
          <div>
            <span className="text-[11px] uppercase tracking-wide text-slate-400">
              Bisherige Kurzbeschreibung
            </span>
            <p className="text-sm text-slate-700" data-testid="ops-sales-catalog-shortdesc-review-old">
              {anzeigeWert(original)}
            </p>
          </div>
          <div>
            <span className="text-[11px] uppercase tracking-wide text-slate-400">
              Neue Kurzbeschreibung
            </span>
            <p className="text-sm text-slate-900" data-testid="ops-sales-catalog-shortdesc-review-new">
              {anzeigeWert(value)}
            </p>
          </div>
          <div>
            <span className="text-[11px] uppercase tracking-wide text-slate-400">
              Bisherige kategorie
            </span>
            <p className="text-sm text-slate-700" data-testid="ops-sales-catalog-shortdesc-review-kategorie-old">
              {anzeigeWert(kategorieOriginal)}
            </p>
          </div>
          <div>
            <span className="text-[11px] uppercase tracking-wide text-slate-400">
              Neue kategorie
            </span>
            <p className="text-sm text-slate-900" data-testid="ops-sales-catalog-shortdesc-review-kategorie-new">
              {anzeigeWert(kategorieValue)}
            </p>
          </div>
          <div>
            <span className="text-[11px] uppercase tracking-wide text-slate-400">
              Bisherige verkaufsart
            </span>
            <p className="text-sm text-slate-700" data-testid="ops-sales-catalog-shortdesc-review-verkaufsart-old">
              {anzeigeWert(verkaufsartOriginal)}
            </p>
          </div>
          <div>
            <span className="text-[11px] uppercase tracking-wide text-slate-400">
              Neue verkaufsart
            </span>
            <p className="text-sm text-slate-900" data-testid="ops-sales-catalog-shortdesc-review-verkaufsart-new">
              {anzeigeWert(verkaufsartValue)}
            </p>
          </div>
          <div>
            <span className="text-[11px] uppercase tracking-wide text-slate-400">
              Bisherige long_description
            </span>
            <p className="text-sm text-slate-700" data-testid="ops-sales-catalog-shortdesc-review-longdesc-old">
              {anzeigeWert(longDescOriginal)}
            </p>
          </div>
          <div>
            <span className="text-[11px] uppercase tracking-wide text-slate-400">
              Neue long_description
            </span>
            <p className="text-sm text-slate-900" data-testid="ops-sales-catalog-shortdesc-review-longdesc-new">
              {anzeigeWert(longDescValue)}
            </p>
          </div>
          <div>
            <span className="text-[11px] uppercase tracking-wide text-slate-400">
              Bisherige per_seat_net_cents
            </span>
            <p className="text-sm text-slate-700" data-testid="ops-sales-catalog-shortdesc-review-perseat-old">
              {perSeatOriginal === null || perSeatOriginal === undefined
                ? NICHT_HINTERLEGT
                : String(perSeatOriginal)}
            </p>
          </div>
          <div>
            <span className="text-[11px] uppercase tracking-wide text-slate-400">
              Neue per_seat_net_cents
            </span>
            <p className="text-sm text-slate-900" data-testid="ops-sales-catalog-shortdesc-review-perseat-new">
              {perSeatValue.trim() === "" ? NICHT_HINTERLEGT : perSeatValue.trim()}
            </p>
          </div>
          <div>
            <span className="text-[11px] uppercase tracking-wide text-slate-400">
              Bisherige billing_intervals
            </span>
            <p className="text-sm text-slate-700" data-testid="ops-sales-catalog-shortdesc-review-intervals-old">
              {intervalsOriginal && intervalsOriginal.length
                ? intervalsOriginal.join(", ")
                : NICHT_HINTERLEGT}
            </p>
          </div>
          <div>
            <span className="text-[11px] uppercase tracking-wide text-slate-400">
              Neue billing_intervals
            </span>
            <p className="text-sm text-slate-900" data-testid="ops-sales-catalog-shortdesc-review-intervals-new">
              {intervalsValue && intervalsValue.length ? intervalsValue.join(", ") : NICHT_HINTERLEGT}
            </p>
          </div>
          <div>
            <span className="text-[11px] uppercase tracking-wide text-slate-400">
              Bisherige stripe_price_ids
            </span>
            <p className="text-sm text-slate-700" data-testid="ops-sales-catalog-shortdesc-review-stripe-old">
              {stripeOriginal && Object.keys(stripeOriginal).length
                ? Object.entries(stripeOriginal)
                    .map(([k, v]) => `${k}: ${v}`)
                    .join(", ")
                : NICHT_HINTERLEGT}
            </p>
          </div>
          <div>
            <span className="text-[11px] uppercase tracking-wide text-slate-400">
              Neue stripe_price_ids
            </span>
            <p className="text-sm text-slate-900" data-testid="ops-sales-catalog-shortdesc-review-stripe-new">
              {(() => {
                const obj = stripePairsToObject(stripePairs);
                const entries = Object.entries(obj);
                return entries.length
                  ? entries.map(([k, v]) => `${k}: ${v}`).join(", ")
                  : NICHT_HINTERLEGT;
              })()}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="ops-sales-catalog-shortdesc-back"
              variant="outline"
              size="sm"
              onClick={onBack}
              disabled={busy}
            >
              Zurück zur Bearbeitung
            </Button>
            <Button
              data-testid="ops-sales-catalog-shortdesc-save"
              size="sm"
              onClick={onSave}
              disabled={busy}
            >
              Änderung speichern
            </Button>
          </div>
        </div>
      )}

      {busy && (
        <div
          className="flex items-center gap-2 text-sm text-slate-500"
          data-testid="ops-sales-catalog-shortdesc-busy"
        >
          <Loader2 className="w-4 h-4 animate-spin" />
          Änderung wird gespeichert …
        </div>
      )}

      {err && (
        <div
          className="flex items-start justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-3 py-2"
          data-testid="ops-sales-catalog-shortdesc-error"
        >
          <p className="text-sm text-red-700 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            {err}
          </p>
          {konfliktReload && (
            <Button
              data-testid="ops-sales-catalog-shortdesc-reload"
              variant="outline"
              size="sm"
              onClick={onReloadDraft}
              disabled={busy}
            >
              Aktuellen Draft neu laden
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// Ein Produktobjekt vollständig lesbar darstellen (Reihenfolge = API-Liste).
// N2E3: `editierbar` blendet GENAU EINE Aktion („Kurzbeschreibung bearbeiten")
// ein — ausschließlich bei Draft-Status der geladenen Version.
function ProduktKarte({ product, editierbar = false, editDisabled = false, onEdit, children }) {
  const pid = product.product_id;
  const t = (suffix) => `ops-sales-catalog-product-${pid}-${suffix}`;
  const preisFeld = (key) => {
    const v = feldWert(product, key);
    return Number.isInteger(v) ? centAnzeige(v, product.currency) : v;
  };
  const tiers = Object.prototype.hasOwnProperty.call(product, "per_seat_tiers")
    ? product.per_seat_tiers
    : undefined;
  const priceIds = Object.prototype.hasOwnProperty.call(product, "stripe_price_ids")
    ? product.stripe_price_ids
    : undefined;
  const intervals = Object.prototype.hasOwnProperty.call(product, "billing_intervals")
    ? product.billing_intervals
    : undefined;

  return (
    <div
      className="rounded-lg border border-slate-200 bg-white px-4 py-3"
      data-testid={`ops-sales-catalog-product-${pid}`}
    >
      <div className="flex flex-wrap items-center gap-2 pb-2 border-b border-slate-100">
        <span className="text-xs text-slate-400 font-mono" data-testid={t("order")}>
          #{feldWert(product, "order")}
        </span>
        <span className="font-heading text-sm font-semibold text-slate-900" data-testid={t("label")}>
          {feldWert(product, "label")}
        </span>
        <Badge variant="outline" className="font-mono text-[11px]" data-testid={t("product-type")}>
          {feldWert(product, "product_type")}
        </Badge>
        <Badge
          variant="outline"
          className={`text-[11px] ${
            product.active === true
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : "bg-slate-100 text-slate-500 border-slate-200"
          }`}
          data-testid={t("active-chip")}
        >
          active: {boolAnzeige(feldWert(product, "active"))}
        </Badge>
        {editierbar && (
          <Button
            data-testid={`ops-sales-catalog-edit-shortdesc-${pid}`}
            variant="outline"
            size="sm"
            className="ml-auto"
            onClick={onEdit}
            disabled={editDisabled}
          >
            <Pencil className="w-3.5 h-3.5 mr-1.5" />
            Produkt bearbeiten
          </Button>
        )}
      </div>

      <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 pt-2">
        <FeldZeile feldId={t("product-id")} label="product_id">
          <span className="font-mono">{feldWert(product, "product_id")}</span>
        </FeldZeile>
        <FeldZeile feldId={t("short-description")} label="short_description">
          {feldWert(product, "short_description")}
        </FeldZeile>
        <FeldZeile feldId={t("long-description")} label="long_description">
          {feldWert(product, "long_description")}
        </FeldZeile>
        <FeldZeile feldId={t("pricing-model")} label="pricing_model">
          {feldWert(product, "pricing_model")}
        </FeldZeile>
        <FeldZeile feldId={t("currency")} label="currency">
          {feldWert(product, "currency")}
        </FeldZeile>
        <FeldZeile feldId={t("billing-intervals")} label="billing_intervals">
          {intervals === undefined
            ? NICHT_GELIEFERT
            : intervals.length
              ? intervals.join(", ")
              : NICHT_HINTERLEGT}
        </FeldZeile>
        <FeldZeile feldId={t("one-time-net-cents")} label="one_time_net_cents">
          {preisFeld("one_time_net_cents")}
        </FeldZeile>
        <FeldZeile feldId={t("base-fee-net-cents")} label="base_fee_net_cents">
          {preisFeld("base_fee_net_cents")}
        </FeldZeile>
        <FeldZeile feldId={t("per-seat-net-cents")} label="per_seat_net_cents">
          {preisFeld("per_seat_net_cents")}
        </FeldZeile>
        <FeldZeile feldId={t("per-seat-tiers")} label="per_seat_tiers">
          {tiers === undefined ? (
            NICHT_GELIEFERT
          ) : tiers.length ? (
            <ul className="space-y-0.5">
              {tiers.map((tier, i) => (
                <li key={i} className="font-mono text-xs">
                  bis {tier.up_to === null || tier.up_to === undefined ? "∞" : tier.up_to}:{" "}
                  {centAnzeige(tier.unit_net_cents, product.currency)}
                </li>
              ))}
            </ul>
          ) : (
            NICHT_HINTERLEGT
          )}
        </FeldZeile>
        <FeldZeile feldId={t("min-price-net-cents")} label="min_price_net_cents">
          {preisFeld("min_price_net_cents")}
        </FeldZeile>
        <FeldZeile feldId={t("max-price-net-cents")} label="max_price_net_cents">
          {preisFeld("max_price_net_cents")}
        </FeldZeile>
        <FeldZeile feldId={t("vat-rate")} label="vat_rate">
          {feldWert(product, "vat_rate")}
        </FeldZeile>
        <FeldZeile feldId={t("vat-note")} label="vat_note">
          {feldWert(product, "vat_note")}
        </FeldZeile>
        <FeldZeile feldId={t("badge")} label="badge">
          {feldWert(product, "badge")}
        </FeldZeile>
        <FeldZeile feldId={t("seat-min")} label="seat_min">
          {feldWert(product, "seat_min")}
        </FeldZeile>
        <FeldZeile feldId={t("seat-max")} label="seat_max">
          {feldWert(product, "seat_max")}
        </FeldZeile>
        <FeldZeile feldId={t("trial-days")} label="trial_days">
          {feldWert(product, "trial_days")}
        </FeldZeile>
        <FeldZeile feldId={t("visible-on-landing")} label="visible_on_landing">
          {boolAnzeige(feldWert(product, "visible_on_landing"))}
        </FeldZeile>
        <FeldZeile feldId={t("visible-on-checkout")} label="visible_on_checkout">
          {boolAnzeige(feldWert(product, "visible_on_checkout"))}
        </FeldZeile>
        <FeldZeile feldId={t("active")} label="active">
          {boolAnzeige(feldWert(product, "active"))}
        </FeldZeile>
        <FeldZeile feldId={t("stripe-product-id")} label="stripe_product_id">
          <span className="font-mono">{feldWert(product, "stripe_product_id")}</span>
        </FeldZeile>
        <FeldZeile feldId={t("stripe-price-ids")} label="stripe_price_ids">
          {priceIds === undefined ? (
            NICHT_GELIEFERT
          ) : Object.keys(priceIds).length ? (
            <ul className="space-y-0.5">
              {Object.entries(priceIds).map(([k, v]) => (
                <li key={k} className="font-mono text-xs">
                  {k}: {v}
                </li>
              ))}
            </ul>
          ) : (
            NICHT_HINTERLEGT
          )}
        </FeldZeile>
        <FeldZeile feldId={t("entitlements")} label="entitlements">
          <StrukturWert product={product} feldKey="entitlements" />
        </FeldZeile>
        <FeldZeile feldId={t("metadata")} label="metadata">
          <StrukturWert product={product} feldKey="metadata" />
        </FeldZeile>
        {/* O1H (Feb 2026) — Betriebsfelder: bei Alt-Produkten „Nicht hinterlegt" /
            „Von der API nicht geliefert"; sonst der gespeicherte deutsche Wert. */}
        <FeldZeile feldId={t("kategorie")} label="kategorie">
          {feldWert(product, "kategorie")}
        </FeldZeile>
        <FeldZeile feldId={t("verkaufsart")} label="verkaufsart">
          {feldWert(product, "verkaufsart")}
        </FeldZeile>
      </dl>
      {children}
    </div>
  );
}

export function SalesCatalogSection() {
  const [versions, setVersions] = useState([]);
  // DEPLOY-02, Betreiberfreigabe 14.10.: maßgebliche Versionen kommen aus dem Backend
  // (sales_catalog_store.current_published/current_draft) – keine eigene Auswahlregel.
  const [aktuelleIds, setAktuelleIds] = useState({ published: null, draft: null });
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailErr, setDetailErr] = useState("");
  // N2C: Zustände der EINZIGEN Schreibaktion (Draft aus bestehender Version erzeugen).
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [draftBusy, setDraftBusy] = useState(false);
  const [draftErr, setDraftErr] = useState("");
  const [draftSuccess, setDraftSuccess] = useState("");
  // N2E3/O1H: Ein-Feld-Editor um zwei zusätzliche Dropdowns erweitert
  // (kategorie, verkaufsart). Es bleibt bei EINEM Editor pro Produkt.
  const [editPid, setEditPid] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [editKategorie, setEditKategorie] = useState(null);
  const [editVerkaufsart, setEditVerkaufsart] = useState(null);
  // OPS-01: vier zusätzliche im Draft editierbare Preisfelder.
  const [editLongDesc, setEditLongDesc] = useState("");
  const [editPerSeat, setEditPerSeat] = useState("");
  const [editIntervals, setEditIntervals] = useState([]);
  const [editStripePairs, setEditStripePairs] = useState([]);
  const [editStep, setEditStep] = useState("edit"); // "edit" | "review"
  const [editBusy, setEditBusy] = useState(false);
  const [editErr, setEditErr] = useState("");
  const [editErrCode, setEditErrCode] = useState(null);
  const [editSuccess, setEditSuccess] = useState("");
  // N2F3: Zustände der Publish-Aktion (nur bei Draft-Status sichtbar).
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishBusy, setPublishBusy] = useState(false);
  const [publishErr, setPublishErr] = useState("");
  const [publishSuccess, setPublishSuccess] = useState("");
  const [publishCacheWarn, setPublishCacheWarn] = useState("");
  // O1E: Zustände der Produktanlage (GENAU EIN vollständiges neues Draft-Produkt).
  const [addOpen, setAddOpen] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  const [addErr, setAddErr] = useState("");
  const [addErrCode, setAddErrCode] = useState(null);
  const [addUnklar, setAddUnklar] = useState(false);
  const [addSuccess, setAddSuccess] = useState("");
  const [addLastPid, setAddLastPid] = useState("");

  const loadVersions = useCallback(() => {
    setLoading(true);
    setLoadErr("");
    opsApi
      .salesCatalogVersions()
      .then((data) => {
        setVersions(data.versions || []);
        setAktuelleIds({
          published: data.current_published_version_id || null,
          draft: data.current_draft_version_id || null,
        });
      })
      .catch((e) => setLoadErr(opsErrorMessage(e)))
      .finally(() => setLoading(false));
  }, []);

  const loadDetail = useCallback((versionId) => {
    setSelectedId(versionId);
    setDetail(null);
    setDetailErr("");
    setDetailLoading(true);
    opsApi
      .salesCatalogVersion(versionId)
      .then(setDetail)
      .catch((e) => setDetailErr(opsErrorMessage(e)))
      .finally(() => setDetailLoading(false));
  }, []);

  useEffect(() => {
    loadVersions();
  }, [loadVersions]);

  // N2C: max. 1 Draft (Backend-Persistenzregel) — bestehender Draft sperrt die Aktion.
  // DEPLOY-02, Betreiberfreigabe 14.10.: bei mehreren Drafts (Altdaten) gilt der jüngste;
  // ältere Drafts sind nur lesbar. Angezeigt wird die zuletzt veröffentlichte Version.
  const draftInList = versions.find((v) => v.version_id === aktuelleIds.draft) || null;
  // N2F3: aktuell veröffentlichte Version (Anzeige im Bestätigungsdialog).
  const publishedInList = versions.find((v) => v.version_id === aktuelleIds.published) || null;
  const detailIstDraft = !!detail && detail.status === "draft";
  const detailBearbeitbar =
    detailIstDraft && (!draftInList || draftInList.version_id === detail.version_id);

  // N2E3/O1H — Editor-Handler. Das geladene Draftobjekt wird NIE lokal mutiert;
  // die gespeicherten Ausgangswerte kommen stets aus `detail` (Backendantwort).
  const editProdukt =
    (editPid && detail && detail.products
      ? detail.products.find((p) => p.product_id === editPid)
      : null) || null;
  const editOriginal =
    editProdukt && typeof editProdukt.short_description === "string"
      ? editProdukt.short_description
      : "";
  const editKategorieOriginal =
    editProdukt && typeof editProdukt.kategorie === "string" ? editProdukt.kategorie : null;
  const editVerkaufsartOriginal =
    editProdukt && typeof editProdukt.verkaufsart === "string" ? editProdukt.verkaufsart : null;
  // OPS-01 — Ausgangswerte der vier neuen Felder (stets aus `detail`).
  const editLongDescOriginal =
    editProdukt && typeof editProdukt.long_description === "string" ? editProdukt.long_description : "";
  const editPerSeatOriginal =
    editProdukt && Number.isInteger(editProdukt.per_seat_net_cents) ? editProdukt.per_seat_net_cents : null;
  const editIntervalsOriginal =
    editProdukt && Array.isArray(editProdukt.billing_intervals) ? editProdukt.billing_intervals : [];
  const editStripeOriginal =
    editProdukt && editProdukt.stripe_price_ids && typeof editProdukt.stripe_price_ids === "object"
      ? editProdukt.stripe_price_ids
      : {};
  const editPerSeatNormalized =
    editPerSeat.trim() === ""
      ? null
      : /^\d+$/.test(editPerSeat.trim())
        ? parseInt(editPerSeat.trim(), 10)
        : NaN;
  // Konsistenzsperre NUR dann aktiv, wenn Intervalle/Stripe tatsächlich
  // geändert wurden (vermeidet Blockade reiner Kurzbeschreibungs-/Preis-Edits
  // an Produkten mit vorbestehendem Alt-Zustand).
  const editIntervalsOrStripeDirty =
    editProdukt !== null &&
    (!arrEqualIgnoreOrder(editIntervals, editIntervalsOriginal) ||
      !shallowObjEqual(stripePairsToObject(editStripePairs), editStripeOriginal));
  const editDirty =
    editProdukt !== null &&
    (editValue !== editOriginal ||
      editKategorie !== editKategorieOriginal ||
      editVerkaufsart !== editVerkaufsartOriginal ||
      editLongDesc !== editLongDescOriginal ||
      editPerSeatNormalized !== editPerSeatOriginal ||
      !arrEqualIgnoreOrder(editIntervals, editIntervalsOriginal) ||
      !shallowObjEqual(stripePairsToObject(editStripePairs), editStripeOriginal));
  const editValidationError =
    perSeatValidierung(editPerSeat) ||
    (editIntervalsOrStripeDirty ? stripeIntervalValidierung(editIntervals, editStripePairs) : null) ||
    null;

  const openEdit = useCallback((p) => {
    setEditPid(p.product_id);
    setEditValue(typeof p.short_description === "string" ? p.short_description : "");
    setEditKategorie(typeof p.kategorie === "string" ? p.kategorie : null);
    setEditVerkaufsart(typeof p.verkaufsart === "string" ? p.verkaufsart : null);
    setEditLongDesc(typeof p.long_description === "string" ? p.long_description : "");
    setEditPerSeat(Number.isInteger(p.per_seat_net_cents) ? String(p.per_seat_net_cents) : "");
    setEditIntervals(Array.isArray(p.billing_intervals) ? [...p.billing_intervals] : []);
    setEditStripePairs(
      p.stripe_price_ids && typeof p.stripe_price_ids === "object"
        ? Object.entries(p.stripe_price_ids).map(([key, value]) => ({ key, value: String(value) }))
        : []
    );
    setEditStep("edit");
    setEditErr("");
    setEditErrCode(null);
    setEditSuccess("");
  }, []);

  const cancelEdit = useCallback(() => {
    setEditPid(null);
    setEditValue("");
    setEditKategorie(null);
    setEditVerkaufsart(null);
    setEditLongDesc("");
    setEditPerSeat("");
    setEditIntervals([]);
    setEditStripePairs([]);
    setEditStep("edit");
    setEditErr("");
    setEditErrCode(null);
  }, []);

  const reviewEdit = useCallback(() => {
    if (!editDirty || editBusy || editValidationError) return;
    setEditErr("");
    setEditErrCode(null);
    setEditStep("review");
  }, [editDirty, editBusy, editValidationError]);

  const backToEdit = useCallback(() => {
    if (editBusy) return;
    setEditStep("edit");
  }, [editBusy]);

  // Konfliktauflösung ausschließlich durch NEULADEN des Drafts (kein Merge,
  // kein Retry): Editor schließen, Liste + Detail erneut vom Backend lesen.
  const reloadDraftAfterConflict = useCallback(() => {
    setEditPid(null);
    setEditValue("");
    setEditKategorie(null);
    setEditVerkaufsart(null);
    setEditLongDesc("");
    setEditPerSeat("");
    setEditIntervals([]);
    setEditStripePairs([]);
    setEditStep("edit");
    setEditErr("");
    setEditErrCode(null);
    loadVersions();
    if (selectedId) loadDetail(selectedId);
  }, [loadVersions, loadDetail, selectedId]);

  // OPS-01 — Handler der vier neuen Felder (reiner lokaler Formularzustand;
  // das geladene Draftobjekt wird NIE mutiert).
  const toggleInterval = useCallback((iv) => {
    setEditIntervals((prev) => (prev.includes(iv) ? prev.filter((x) => x !== iv) : [...prev, iv]));
  }, []);
  const stripeKeyChange = useCallback((i, key) => {
    setEditStripePairs((prev) => prev.map((p, idx) => (idx === i ? { ...p, key } : p)));
  }, []);
  const stripeValueChange = useCallback((i, value) => {
    setEditStripePairs((prev) => prev.map((p, idx) => (idx === i ? { ...p, value } : p)));
  }, []);
  const stripeAdd = useCallback(() => {
    setEditStripePairs((prev) => [...prev, { key: "", value: "" }]);
  }, []);
  const stripeRemove = useCallback((i) => {
    setEditStripePairs((prev) => prev.filter((_, idx) => idx !== i));
  }, []);

  // GENAU EIN PUT über den bestehenden N2E2-Endpunkt; danach ist der erneut
  // GELESENE Draft (Detail-GET) der maßgebliche UI-Zustand. Kein optimistisches
  // Update, kein Retry, Doppelaufruf durch busy-Guard verhindert.
  // O1H: neben `short_description` werden `kategorie` und `verkaufsart` mit
  // demselben CAS-Payload atomar gespeichert (deutsche Werte 1:1, `null` =
  // „nicht gesetzt").
  const saveEdit = useCallback(() => {
    if (editBusy || !detail || !editPid) return;
    setEditBusy(true);
    setEditErr("");
    setEditErrCode(null);
    setEditSuccess("");
    const payload = {
      expected_content_hash: detail.content_hash,
      products: detail.products.map((p) =>
        p.product_id === editPid
          ? {
              ...p,
              short_description: editValue,
              kategorie: editKategorie,
              verkaufsart: editVerkaufsart,
              long_description: editLongDesc,
              per_seat_net_cents:
                editPerSeat.trim() === "" ? null : parseInt(editPerSeat.trim(), 10),
              billing_intervals: editIntervals,
              stripe_price_ids: stripePairsToObject(editStripePairs),
            }
          : p
      ),
    };
    opsApi
      .salesCatalogUpdateDraft(detail.version_id, payload)
      .then(() => opsApi.salesCatalogVersion(detail.version_id))
      .then((fresh) => {
        setDetail(fresh);
        setEditSuccess(
          `Änderungen an ${editPid} (Kurzbeschreibung, Kategorie, Verkaufsart, ` +
            "Langbeschreibung, Preis, Intervalle, Stripe-Price-IDs) wurden gespeichert. " +
            "Neuer Stand wurde vom Backend geladen."
        );
        setEditPid(null);
        setEditValue("");
        setEditKategorie(null);
        setEditVerkaufsart(null);
        setEditLongDesc("");
        setEditPerSeat("");
        setEditIntervals([]);
        setEditStripePairs([]);
        setEditStep("edit");
        loadVersions();
      })
      .catch((e) => {
        const code = katalogFehlerCode(e);
        setEditErrCode(code);
        setEditErr(opsErrorMessage(e));
        if (code === "catalog_version_not_draft") {
          // Version ist kein Draft mehr: Bearbeitung schließen, Stand neu laden.
          setEditPid(null);
          setEditValue("");
          setEditKategorie(null);
          setEditVerkaufsart(null);
          setEditLongDesc("");
          setEditPerSeat("");
          setEditIntervals([]);
          setEditStripePairs([]);
          setEditStep("edit");
          loadVersions();
          loadDetail(detail.version_id);
        } else if (e?.response?.status === 422) {
          // Eingabe zur Korrektur behalten, zurück in den Bearbeitungsschritt.
          setEditStep("edit");
        }
        // stale / product_set_changed: Meldung + „Aktuellen Draft neu laden";
        // 503/401/Netzwerk: Meldung, kein Retry, kein lokaler Erfolg.
      })
      .finally(() => setEditBusy(false));
  }, [
    editBusy,
    detail,
    editPid,
    editValue,
    editKategorie,
    editVerkaufsart,
    editLongDesc,
    editPerSeat,
    editIntervals,
    editStripePairs,
    loadVersions,
    loadDetail,
  ]);

  // N2C: genau EIN POST an den bestehenden Endpunkt, erst nach ausdrücklicher
  // Bestätigung. Kein Retry, kein optimistisches UI — nach Erfolg sind die
  // erneut GELESENEN Backendantworten (Liste + Detail) maßgeblich.
  // O1G: Der Payload enthält KEINE version_id — die neue Versions-ID
  // (YYYY-MM-DD-NNNN) vergibt ausschließlich der Server (atomare Tagessequenz).
  const createDraft = useCallback(() => {
    if (draftBusy || !detail) return;
    setConfirmOpen(false);
    setDraftBusy(true);
    setDraftErr("");
    setDraftSuccess("");
    opsApi
      .salesCatalogCreateDraft({
        products: detail.products,
        notes: `Draft aus Version ${detail.version_id} (Ops Center)`,
      })
      .then((data) => {
        const createdId = data && data.created && data.created.version_id;
        setDraftSuccess(
          `Draft ${createdId || ""} wurde erstellt. Die veröffentlichte Version bleibt unverändert.`
        );
        loadVersions();
        if (createdId) loadDetail(createdId);
      })
      .catch((e) => setDraftErr(opsErrorMessage(e)))
      .finally(() => setDraftBusy(false));
  }, [draftBusy, detail, loadVersions, loadDetail]);

  // N2F3: genau EIN POST an den in N2F2 gehärteten Publish-Endpunkt — nur nach
  // ausdrücklicher Bestätigung, mit dem expected_content_hash des GELADENEN
  // Drafts (CAS). KEIN optimistisches Update: maßgeblich ist ausschließlich der
  // erneut GELESENE Backend-Zustand (Detail-GET + Versionslisten-Reload).
  // Backend-Fehlermeldungen werden unverändert durchgereicht (opsErrorMessage).
  const publishDraft = useCallback(() => {
    if (publishBusy || !detail || detail.status !== "draft") return;
    setPublishBusy(true);
    setPublishErr("");
    setPublishSuccess("");
    setPublishCacheWarn("");
    opsApi
      .salesCatalogPublishDraft(detail.version_id, detail.content_hash)
      .then((res) =>
        opsApi.salesCatalogVersion(detail.version_id).then((fresh) => {
          setDetail(fresh);
          setPublishOpen(false);
          setPublishSuccess("Version erfolgreich veröffentlicht.");
          toast.success("Version erfolgreich veröffentlicht.");
          if (res && res.cache_refresh && res.cache_refresh.ok === false) {
            // Sonderstatus aus N2F2: DB-Publish committed, Provider-Cache nicht
            // aktualisiert. Kein Re-Publish; Code unverändert sichtbar machen.
            setPublishCacheWarn(
              `Veröffentlichung wurde in der Datenbank committed, aber der Provider-Cache ` +
                `wurde nicht aktualisiert (${res.cache_refresh.code || "catalog_published_cache_refresh_failed"}). ` +
                `Kein erneuter Publish erforderlich — Betreiber-Intervention laut Backend erforderlich.`
            );
          }
          loadVersions();
        })
      )
      .catch((e) => {
        setPublishOpen(false);
        setPublishErr(opsErrorMessage(e));
      })
      .finally(() => setPublishBusy(false));
  }, [publishBusy, detail, loadVersions]);

  // O1E: Produktanlage öffnen/abbrechen — reiner lokaler Formularzustand.
  // Abbrechen: KEIN Request, KEINE Mutation der geladenen Draftdaten.
  const openAdd = useCallback(() => {
    setAddErr("");
    setAddErrCode(null);
    setAddUnklar(false);
    setAddSuccess("");
    setAddOpen(true);
  }, []);

  const cancelAdd = useCallback(() => {
    if (addBusy) return;
    setAddOpen(false);
    setAddErr("");
    setAddErrCode(null);
    setAddUnklar(false);
  }, [addBusy]);

  // O1E: stale-Konflikt ausschließlich durch NEULADEN auflösen — Maske schließen,
  // KEINE automatische Übernahme der Formulardaten auf den neuen Hash.
  const reloadAfterAddConflict = useCallback(() => {
    setAddOpen(false);
    setAddErr("");
    setAddErrCode(null);
    setAddUnklar(false);
    loadVersions();
    if (selectedId) loadDetail(selectedId);
  }, [loadVersions, loadDetail, selectedId]);

  // O1E: GENAU EIN POST an den bestehenden O1D-Endpunkt — erst nach der
  // ausdrücklichen Bestätigung in der Maske. Kein Retry, kein optimistisches
  // Produkt; nach Erfolg ist AUSSCHLIESSLICH der erneut GELESENE Backend-Zustand
  // (Detail-GET + Versionslisten-Reload) maßgeblich.
  const submitAddProduct = useCallback(
    (produkt) => {
      if (addBusy || !detail || detail.status !== "draft" || !detail.content_hash) return;
      setAddBusy(true);
      setAddErr("");
      setAddErrCode(null);
      setAddUnklar(false);
      setAddSuccess("");
      setAddLastPid(produkt.product_id);
      opsApi
        .salesCatalogAddDraftProduct(detail.version_id, {
          expected_content_hash: detail.content_hash,
          product: produkt,
        })
        .then(() => opsApi.salesCatalogVersion(detail.version_id))
        .then((fresh) => {
          setDetail(fresh);
          setAddOpen(false);
          setAddSuccess(
            `Produkt ${produkt.product_id} wurde dem Draft hinzugefügt. ` +
              "Neuer Stand wurde vom Backend geladen."
          );
          loadVersions();
        })
        .catch((e) => {
          const status = e?.response?.status;
          if (!status || (status >= 500 && status !== 503)) {
            // Unklares Requestergebnis: KEIN zweiter POST — zuerst Draft per GET prüfen.
            setAddUnklar(true);
            return;
          }
          const code = katalogFehlerCode(e);
          setAddErrCode(code);
          setAddErr(opsErrorMessage(e));
          if (code === "catalog_version_not_draft") {
            setAddOpen(false);
            loadVersions();
            loadDetail(detail.version_id);
          } else if (status === 404) {
            setAddOpen(false);
            loadVersions();
          }
          // stale: Meldung + „Aktuellen Draft neu laden"; already_exists/422/503/401:
          // Meldung anzeigen, Eingaben bleiben zur bewussten Korrektur erhalten.
        })
        .finally(() => setAddBusy(false));
    },
    [addBusy, detail, loadVersions, loadDetail]
  );

  // O1E: unklares POST-Ergebnis — Draft per GET neu laden und prüfen, ob die
  // product_id vorhanden ist. Erst danach Zustand anzeigen; KEIN zweiter POST.
  const checkAddUnklar = useCallback(() => {
    if (addBusy || !detail) return;
    setAddBusy(true);
    opsApi
      .salesCatalogVersion(detail.version_id)
      .then((fresh) => {
        setDetail(fresh);
        setAddUnklar(false);
        const vorhanden = (fresh.products || []).some((p) => p.product_id === addLastPid);
        if (vorhanden) {
          setAddOpen(false);
          setAddErr("");
          setAddErrCode(null);
          setAddSuccess(
            `Produkt ${addLastPid} wurde dem Draft hinzugefügt (per GET-Reload bestätigt).`
          );
          loadVersions();
        } else {
          setAddErr(
            `Produkt ${addLastPid} ist im neu geladenen Draft nicht vorhanden — ` +
              "es wurde nicht angelegt. Eingaben bleiben zur bewussten Korrektur erhalten."
          );
        }
      })
      .catch((e) => setAddErr(opsErrorMessage(e)))
      .finally(() => setAddBusy(false));
  }, [addBusy, detail, addLastPid, loadVersions]);

  return (
    <Card
      className="bg-white border border-slate-200 shadow-sm overflow-hidden"
      data-testid="ops-sales-catalog-section"
    >
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <BookOpenCheck className="w-5 h-5 text-slate-500" />
          <div>
            <h2 className="font-heading text-lg font-semibold text-slate-900">Sales-Katalog</h2>
            <p className="text-xs text-slate-500">
              Read-only Bestandssicht auf den DB-Sales-Katalog (Versionen und
              Produktobjekte). Keine Bearbeitung – reine Anzeige der gespeicherten Werte.
            </p>
          </div>
        </div>
        <Button
          data-testid="ops-sales-catalog-refresh"
          variant="outline"
          size="sm"
          onClick={loadVersions}
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Aktualisieren
        </Button>
      </div>

      {loading && (
        <div
          className="px-5 py-10 flex items-center justify-center text-slate-400"
          data-testid="ops-sales-catalog-loading"
        >
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      )}

      {!loading && loadErr && (
        <div className="px-5 py-6" data-testid="ops-sales-catalog-error">
          <div className="flex items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3">
            <p className="text-sm text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              {loadErr}
            </p>
            <Button variant="outline" size="sm" onClick={loadVersions}>
              Erneut versuchen
            </Button>
          </div>
        </div>
      )}

      {!loading && !loadErr && versions.length === 0 && (
        <div
          className="px-5 py-10 text-center text-sm text-slate-400"
          data-testid="ops-sales-catalog-empty"
        >
          Keine Sales-Katalog-Versionen vorhanden.
        </div>
      )}

      {!loading && !loadErr && versions.length > 0 && (
        <div className="overflow-x-auto">
          <Table data-testid="ops-sales-catalog-versions-table">
            <TableHeader>
              <TableRow>
                <TableHead>Version</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Erstellt</TableHead>
                <TableHead>Publiziert</TableHead>
                <TableHead>Gültig von</TableHead>
                <TableHead>Gültig bis</TableHead>
                <TableHead className="text-right">Produkte</TableHead>
                <TableHead>content_hash</TableHead>
                <TableHead className="text-right">Ansicht</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {versions.map((v) => (
                <TableRow
                  key={v.version_id}
                  data-testid={`ops-sales-catalog-version-row-${v.version_id}`}
                  className={selectedId === v.version_id ? "bg-indigo-50/50" : ""}
                >
                  <TableCell className="font-mono text-xs font-medium text-slate-900">
                    <span className="inline-flex items-center gap-1.5">
                      {v.status === "published" && (
                        <span
                          className="inline-flex items-center text-emerald-600"
                          data-testid={`ops-sales-catalog-published-marker-${v.version_id}`}
                          title="Aktuell veröffentlichte Version"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" />
                        </span>
                      )}
                      {v.version_id}
                    </span>
                  </TableCell>
                  <TableCell>
                    <StatusBadgeSc status={v.status} />
                  </TableCell>
                  <TableCell className="text-xs text-slate-500 font-mono">
                    {feldWert(v, "created_at")}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500 font-mono">
                    {feldWert(v, "published_at")}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500 font-mono">
                    {feldWert(v, "valid_from")}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500 font-mono">
                    {feldWert(v, "valid_until")}
                  </TableCell>
                  <TableCell className="text-right text-xs text-slate-700">
                    {feldWert(v, "product_count")}
                  </TableCell>
                  <TableCell className="text-[10px] text-slate-400 font-mono break-all max-w-[160px]">
                    {feldWert(v, "content_hash")}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      data-testid={`ops-sales-catalog-select-${v.version_id}`}
                      variant="outline"
                      size="sm"
                      onClick={() => loadDetail(v.version_id)}
                      disabled={
                        (detailLoading && selectedId === v.version_id) ||
                        editPid !== null ||
                        addOpen
                      }
                      title={
                        editPid !== null || addOpen
                          ? "Offene Bearbeitung zuerst abbrechen oder speichern."
                          : undefined
                      }
                    >
                      <Eye className="w-3.5 h-3.5 mr-1.5" />
                      Anzeigen
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {selectedId && (
        <div className="border-t border-slate-100 px-5 py-4 space-y-3" data-testid="ops-sales-catalog-detail">
          <div className="flex items-center gap-2">
            <h3 className="font-heading text-sm font-semibold text-slate-900">
              Produkte der Version <span className="font-mono">{selectedId}</span>
            </h3>
            {detail && <StatusBadgeSc status={detail.status} />}
          </div>

          {draftBusy && (
            <div
              className="flex items-center gap-2 text-sm text-slate-500 rounded-md border border-slate-200 bg-slate-50 px-3 py-2"
              data-testid="ops-sales-catalog-draft-busy"
            >
              <Loader2 className="w-4 h-4 animate-spin" />
              Draft wird erstellt …
            </div>
          )}

          {draftErr && (
            <div
              className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              data-testid="ops-sales-catalog-draft-error"
            >
              <AlertTriangle className="w-4 h-4 shrink-0" />
              {draftErr}
            </div>
          )}

          {draftSuccess && (
            <div
              className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700"
              data-testid="ops-sales-catalog-draft-success"
            >
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              {draftSuccess}
            </div>
          )}

          {editSuccess && (
            <div
              className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700"
              data-testid="ops-sales-catalog-shortdesc-success"
            >
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              {editSuccess}
            </div>
          )}

          {publishErr && (
            <div
              className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              data-testid="ops-sales-catalog-publish-error"
            >
              <AlertTriangle className="w-4 h-4 shrink-0" />
              {publishErr}
            </div>
          )}

          {publishSuccess && (
            <div
              className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700"
              data-testid="ops-sales-catalog-publish-success"
            >
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              {publishSuccess}
            </div>
          )}

          {publishCacheWarn && (
            <div
              className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
              data-testid="ops-sales-catalog-publish-cache-warning"
            >
              <AlertTriangle className="w-4 h-4 shrink-0" />
              {publishCacheWarn}
            </div>
          )}

          {addSuccess && (
            <div
              className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700"
              data-testid="ops-sales-catalog-add-success"
            >
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              {addSuccess}
            </div>
          )}

          {addErr && !addOpen && (
            <div
              className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              data-testid="ops-sales-catalog-add-error"
            >
              <AlertTriangle className="w-4 h-4 shrink-0" />
              {addErr}
            </div>
          )}

          {!detailLoading && !detailErr && detailIstDraft && !detailBearbeitbar && (
            <p
              className="text-xs text-amber-700 rounded-md border border-amber-200 bg-amber-50 px-3 py-2"
              data-testid="ops-sales-catalog-older-draft-hint"
            >
              Älterer Entwurf – nur lesbar. Bearbeitet wird der jüngste Entwurf (
              <span className="font-mono">{draftInList.version_id}</span>).
            </p>
          )}

          {!detailLoading && !detailErr && detailBearbeitbar && (
            <div data-testid="ops-sales-catalog-publish-area">
              <Button
                data-testid="ops-sales-catalog-publish-button"
                size="sm"
                onClick={() => {
                  setPublishErr("");
                  setPublishSuccess("");
                  setPublishCacheWarn("");
                  setPublishOpen(true);
                }}
                disabled={publishBusy || editPid !== null || addOpen}
                title={
                  editPid !== null || addOpen
                    ? "Offene Bearbeitung zuerst abbrechen oder speichern."
                    : undefined
                }
              >
                <UploadCloud className="w-3.5 h-3.5 mr-1.5" />
                Veröffentlichen
              </Button>
            </div>
          )}

          {/* O1E: Produktanlage — AUSSCHLIESSLICH bei geladenem Draft MIT content_hash.
              Kein Auto-Draft, keine Kopplung mit der N2C-Draft-Erzeugung. */}
          {!detailLoading && !detailErr && detailBearbeitbar && !!detail.content_hash && (
            <div data-testid="ops-sales-catalog-add-product-area" className="space-y-3">
              {!addOpen && (
                <Button
                  data-testid="ops-sales-catalog-add-product-button"
                  variant="outline"
                  size="sm"
                  onClick={openAdd}
                  disabled={addBusy || publishBusy || draftBusy || editBusy || editPid !== null}
                  title={
                    editPid !== null
                      ? "Offene Bearbeitung zuerst abbrechen oder speichern."
                      : undefined
                  }
                >
                  <PackagePlus className="w-3.5 h-3.5 mr-1.5" />
                  Produkt anlegen
                </Button>
              )}
              {addOpen && (
                <ProduktAnlageMaske
                  versionId={detail.version_id}
                  contentHash={detail.content_hash}
                  busy={addBusy}
                  err={addErr}
                  errCode={addErrCode}
                  unklar={addUnklar}
                  onCancel={cancelAdd}
                  onSubmit={submitAddProduct}
                  onReloadDraft={reloadAfterAddConflict}
                  onCheckUnklar={checkAddUnklar}
                />
              )}
            </div>
          )}

          {!detailLoading && !detailErr && detail && detail.status !== "draft" && (
            <div data-testid="ops-sales-catalog-draft-area">
              {draftInList ? (
                <p
                  className="text-xs text-slate-500 rounded-md border border-slate-200 bg-slate-50 px-3 py-2"
                  data-testid="ops-sales-catalog-draft-exists-hint"
                >
                  Es existiert bereits ein Draft (
                  <span className="font-mono">{draftInList.version_id}</span>). Eine weitere
                  Draft-Erstellung ist nicht möglich, solange dieser Draft besteht.
                </p>
              ) : (
                <Button
                  data-testid="ops-sales-catalog-create-draft-button"
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmOpen(true)}
                  disabled={draftBusy}
                >
                  <FilePlus2 className="w-3.5 h-3.5 mr-1.5" />
                  Draft aus dieser Version erstellen
                </Button>
              )}
            </div>
          )}

          {detailLoading && (
            <div
              className="py-8 flex items-center justify-center text-slate-400"
              data-testid="ops-sales-catalog-detail-loading"
            >
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          )}

          {!detailLoading && detailErr && (
            <div data-testid="ops-sales-catalog-detail-error">
              <div className="flex items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3">
                <p className="text-sm text-red-700 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  {detailErr}
                </p>
                <Button variant="outline" size="sm" onClick={() => loadDetail(selectedId)}>
                  Erneut versuchen
                </Button>
              </div>
            </div>
          )}

          {!detailLoading && !detailErr && detail && detail.products && detail.products.length === 0 && (
            <div
              className="py-8 text-center text-sm text-slate-400"
              data-testid="ops-sales-catalog-products-empty"
            >
              Diese Version enthält keine Produkte.
            </div>
          )}

          {!detailLoading && !detailErr && detail && detail.products && detail.products.length > 0 && (
            <div className="space-y-3" data-testid="ops-sales-catalog-products">
              {detail.products.map((p) => (
                <ProduktKarte
                  key={p.product_id}
                  product={p}
                  editierbar={detailBearbeitbar}
                  editDisabled={editPid !== null || editBusy || addOpen}
                  onEdit={() => openEdit(p)}
                >
                  {editPid === p.product_id && (
                    <KurzbeschreibungEditor
                      product={p}
                      value={editValue}
                      original={editOriginal}
                      kategorieValue={editKategorie}
                      kategorieOriginal={editKategorieOriginal}
                      verkaufsartValue={editVerkaufsart}
                      verkaufsartOriginal={editVerkaufsartOriginal}
                      longDescValue={editLongDesc}
                      longDescOriginal={editLongDescOriginal}
                      perSeatValue={editPerSeat}
                      perSeatOriginal={editPerSeatOriginal}
                      intervalsValue={editIntervals}
                      intervalsOriginal={editIntervalsOriginal}
                      stripePairs={editStripePairs}
                      stripeOriginal={editStripeOriginal}
                      validationError={editValidationError}
                      dirty={editDirty}
                      step={editStep}
                      busy={editBusy}
                      err={editErr}
                      errCode={editErrCode}
                      onChange={setEditValue}
                      onKategorieChange={setEditKategorie}
                      onVerkaufsartChange={setEditVerkaufsart}
                      onLongDescChange={setEditLongDesc}
                      onPerSeatChange={setEditPerSeat}
                      onToggleInterval={toggleInterval}
                      onStripeKeyChange={stripeKeyChange}
                      onStripeValueChange={stripeValueChange}
                      onStripeAdd={stripeAdd}
                      onStripeRemove={stripeRemove}
                      onCancel={cancelEdit}
                      onReview={reviewEdit}
                      onBack={backToEdit}
                      onSave={saveEdit}
                      onReloadDraft={reloadDraftAfterConflict}
                    />
                  )}
                </ProduktKarte>
              ))}
            </div>
          )}
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="ops-sales-catalog-draft-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Draft erstellen</AlertDialogTitle>
            <AlertDialogDescription data-testid="ops-sales-catalog-draft-dialog-text">
              Aus der Version <span className="font-mono">{selectedId}</span> wird ein neuer
              Draft erstellt — ein neuer, später bearbeitbarer Arbeitsstand. Die neue
              Versions-ID wird beim Erstellen automatisch und kollisionsfrei vom Server
              vergeben. Die aktuell veröffentlichte Version bleibt dadurch unverändert.
              In dieser Stufe erfolgt noch keine Veröffentlichung. Draft jetzt erstellen?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ops-sales-catalog-draft-cancel">
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="ops-sales-catalog-draft-confirm"
              onClick={createDraft}
              disabled={draftBusy}
            >
              Draft erstellen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        open={publishOpen}
        onOpenChange={(o) => {
          // Während des laufenden Requests ist der Dialog nicht schließbar
          // und nicht mehrfach absendbar.
          if (!publishBusy) setPublishOpen(o);
        }}
      >
        <AlertDialogContent data-testid="ops-sales-catalog-publish-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Version veröffentlichen</AlertDialogTitle>
            <AlertDialogDescription data-testid="ops-sales-catalog-publish-dialog-text">
              Die aktuell veröffentlichte Version wird archiviert. Reine
              Statusumschaltung — es werden keine Produktdaten, Preise oder
              Stripe-Zuordnungen verändert. Version jetzt veröffentlichen?
            </AlertDialogDescription>
          </AlertDialogHeader>
          {detail && (
            <dl
              className="space-y-1.5 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
              data-testid="ops-sales-catalog-publish-dialog-info"
            >
              <div className="flex flex-col">
                <dt className="text-[11px] uppercase tracking-wide text-slate-400">Version</dt>
                <dd className="font-mono text-slate-800" data-testid="ops-sales-catalog-publish-dialog-version">
                  {feldWert(detail, "version_id")}
                </dd>
              </div>
              <div className="flex flex-col">
                <dt className="text-[11px] uppercase tracking-wide text-slate-400">Status</dt>
                <dd className="text-slate-800" data-testid="ops-sales-catalog-publish-dialog-status">
                  {feldWert(detail, "status")}
                </dd>
              </div>
              <div className="flex flex-col">
                <dt className="text-[11px] uppercase tracking-wide text-slate-400">content_hash</dt>
                <dd
                  className="font-mono text-xs text-slate-800 break-all"
                  data-testid="ops-sales-catalog-publish-dialog-hash"
                >
                  {feldWert(detail, "content_hash")}
                </dd>
              </div>
              <div className="flex flex-col">
                <dt className="text-[11px] uppercase tracking-wide text-slate-400">Produktanzahl</dt>
                <dd className="text-slate-800" data-testid="ops-sales-catalog-publish-dialog-product-count">
                  {Array.isArray(detail.products)
                    ? detail.products.length
                    : feldWert(detail, "product_count")}
                </dd>
              </div>
              <div className="flex flex-col">
                <dt className="text-[11px] uppercase tracking-wide text-slate-400">
                  Aktuell veröffentlichte Version
                </dt>
                <dd
                  className="font-mono text-slate-800"
                  data-testid="ops-sales-catalog-publish-dialog-current-published"
                >
                  {publishedInList ? publishedInList.version_id : "Keine veröffentlichte Version vorhanden"}
                </dd>
              </div>
            </dl>
          )}
          {publishBusy && (
            <div
              className="flex items-center gap-2 text-sm text-slate-500"
              data-testid="ops-sales-catalog-publish-busy"
            >
              <Loader2 className="w-4 h-4 animate-spin" />
              Version wird veröffentlicht …
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="ops-sales-catalog-publish-cancel"
              disabled={publishBusy}
            >
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="ops-sales-catalog-publish-confirm"
              disabled={publishBusy}
              onClick={(e) => {
                // Dialog bleibt während des Requests geöffnet (Spinner);
                // geschlossen wird erst nach Backend-Ergebnis.
                e.preventDefault();
                publishDraft();
              }}
            >
              Veröffentlichen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
